import { useState, useEffect } from 'react';
import { api, ShopifyProduct, ProductMetric, MetricsRange } from '../api';
import Layout from '../components/Layout';

function formatCurrency(amount: number, currency = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount);
}

export default function InfluencerProducts() {
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [metrics, setMetrics] = useState<ProductMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [range, setRange] = useState<MetricsRange>('30d');

  useEffect(() => {
    loadData();
  }, [range]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [productsRes, metricsRes] = await Promise.all([
        api.getMyShopifyProducts(),
        api.getProductMetrics(range),
      ]);
      setProducts(productsRes.products);
      setMetrics(metricsRes.products);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Merge product info with metrics
  const productsWithMetrics = products.map((product) => {
    const metric = metrics.find(
      (m) => m.shopify_product_id === product.shopify_product_id
    );
    return {
      ...product,
      total_units: metric?.total_units || 0,
      total_revenue: metric?.total_revenue || 0,
      currency: metric?.currency || 'USD',
    };
  });

  // Sort by revenue
  productsWithMetrics.sort((a, b) => b.total_revenue - a.total_revenue);

  // Calculate totals
  const totalRevenue = productsWithMetrics.reduce((sum, p) => sum + p.total_revenue, 0);
  const totalUnits = productsWithMetrics.reduce((sum, p) => sum + p.total_units, 0);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-accent-500/30 border-t-accent-500 rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-display font-bold text-ink-50">My Products</h1>
            <p className="text-ink-400 mt-1">Track performance of your products</p>
          </div>
          <div className="flex gap-2">
            {(['7d', '30d', '90d', 'all'] as MetricsRange[]).map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  range === r
                    ? 'bg-accent-500 text-white'
                    : 'bg-ink-800 text-ink-300 hover:bg-ink-700'
                }`}
              >
                {r === 'all' ? 'All Time' : r}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="bg-accent-500/10 border border-accent-500/20 rounded-lg p-4 text-accent-400">
            {error}
          </div>
        )}

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="card p-6">
            <div className="text-sm text-ink-400 mb-1">Total Products</div>
            <div className="text-3xl font-bold text-ink-100">{products.length}</div>
          </div>
          <div className="card p-6">
            <div className="text-sm text-ink-400 mb-1">Units Sold</div>
            <div className="text-3xl font-bold text-ink-100">{totalUnits}</div>
          </div>
          <div className="card p-6">
            <div className="text-sm text-ink-400 mb-1">Total Revenue</div>
            <div className="text-3xl font-bold text-accent-400">{formatCurrency(totalRevenue)}</div>
          </div>
        </div>

        {/* Products Table */}
        {products.length === 0 ? (
          <div className="card p-12 text-center">
            <svg
              className="w-16 h-16 text-ink-600 mx-auto mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
            <h3 className="text-lg font-medium text-ink-200 mb-2">No products yet</h3>
            <p className="text-ink-400 mb-4">
              Request products from our catalog to get started.
            </p>
            <a href="/explore" className="btn-primary inline-flex">
              Explore Products
            </a>
          </div>
        ) : (
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-ink-800">
                    <th className="text-left px-4 py-3 text-xs font-medium text-ink-400 uppercase tracking-wider">
                      Product
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-ink-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-ink-400 uppercase tracking-wider">
                      Units Sold
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-ink-400 uppercase tracking-wider">
                      Revenue
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-800">
                  {productsWithMetrics.map((product) => (
                    <tr key={product.id} className="hover:bg-ink-800/50 transition-colors">
                      <td className="px-4 py-4">
                        <div>
                          <div className="font-medium text-ink-100">
                            {product.title || 'Untitled Product'}
                          </div>
                          {product.handle && (
                            <div className="text-sm text-ink-400">/{product.handle}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            product.status === 'active'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-ink-600/50 text-ink-400'
                          }`}
                        >
                          {product.status || 'unknown'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <span className="text-ink-200 font-medium">{product.total_units}</span>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <span className="text-accent-400 font-bold">
                          {formatCurrency(product.total_revenue, product.currency)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
